import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { COMMON_ENV } from '@common/constants/common-env.constant';
import { CONST } from '@common/constants/const.constant';
import { THEME } from '@common/constants/theme.constant';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Icon } from '@common/general-ui/icon/icon.component';
import { EMPTY_LOQATE_ADDRESS, IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { ISelectOption } from '@common/general-ui/select/select.component';
import { StatusTagV2 } from '@common/general-ui/tag-v2/status-tag-v2.component';
import { useModalEx } from '@common/hooks/use-modal-ex.hook';
import { usePromise } from '@common/hooks/use-promise.hook';
import { useScreenMediaQuery } from '@common/hooks/use-screen-media-query.hook';
import { useTimeout } from '@common/hooks/use-timeout.hook';
import { Global } from '@emotion/react';
import { AutoComplete, Input } from 'antd';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from 'twin.macro';
import { ManualAddressModal } from './manual-address-modal.component';

export type IInputLoqateChangeHandler = (arg: { name: string; value: IInputLoqateValue }) => void;
export interface IGetAddressIOProps<TName = string> extends IFieldProps<IInputLoqateValue, TName> {
    className?: string;
    onChange?: IInputLoqateChangeHandler;
    onBlur?: (e: React.FocusEvent) => void;
    apiKey?: string;
}

interface IValidateItem {
    address: string;
    id: string;
    url: string;
}

interface IGetItem {
    postcode: string;
    latitude: number;
    longitude: number;
    formatted_address: string[];
    thoroughfare: string;
    building_name: string;
    sub_building_name: string;
    sub_building_number: string;
    building_number: string;
    line_1: string;
    line_2: string;
    line_3: string;
    line_4: string;
    locality: string;
    town_or_city: string;
    county: string;
    district: string;
    country: string;
    residential: boolean;
}

const DEBOUNCE_DELAY = 800;
export const GetAddressIO = ({
    name,
    errorMessage,
    helperText,
    placeholder,
    onChange,
    onBlur,
    className,
    defaultValue,
    apiKey = COMMON_ENV.REACT_APP_GET_ADDRESS_IO_API_KEY || '',
    ...rest
}: IGetAddressIOProps): JSX.Element => {
    const [value, setValue] = useState<IInputLoqateValue | undefined>(rest.value);
    const [selectedSuggestion, setSelectedSuggestion] = useState<IValidateItem>();
    const [allSuggestions, setAllSuggestions] = useState<IValidateItem[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<string | undefined>(undefined);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { isDownMD } = useScreenMediaQuery();

    useEffect(() => {
        if (rest.value?.originalLabel) {
            setSelectedAddress(rest.value?.originalLabel);
        }
    }, [rest.value?.originalLabel]);

    const {
        start: startSuggestionsPromise,
        reset: resetPromise,
        success: suggestionsSuccess,
        errors: suggestionsError,
    } = usePromise<[IValidateItem[]]>();

    useEffect(() => {
        if (!rest.value) {
            setValue(rest.value);
            setSelectedSuggestion(undefined);
            setAllSuggestions([]);
        }
    }, [rest.value]);

    const handleClearClick = useCallback(() => {
        resetPromise();
        onChange && onChange({ name, value: EMPTY_LOQATE_ADDRESS });
        setSelectedSuggestion(undefined);
        setSelectedAddress(undefined);
        setAllSuggestions([]);
        setValue(EMPTY_LOQATE_ADDRESS);
    }, [onChange, setSelectedSuggestion, setAllSuggestions, name, resetPromise]);

    useEffect(() => {
        if (selectedSuggestion) {
            axios.get<IGetItem>(`https://api.getAddress.io/get/${selectedSuggestion.id}?api-key=${apiKey} `).then((res) => {
                onChange &&
                    onChange({
                        name,
                        value: {
                            addressLine1: res.data.line_1,
                            addressLine2: res.data.line_2,
                            addressLine3: res.data.line_3,
                            city: res.data.town_or_city,
                            country: CONST.DEFAULT_COUNTRY_ABBR, // BE have validation max 2 sybmols (abbr) res.data.country,
                            label: selectedSuggestion.address,
                            originalLabel: `${selectedSuggestion.address}`,
                            postCode: res.data.postcode,
                        },
                    });
            });
        }
    }, [selectedSuggestion, apiKey, name, onChange]);

    const { start } = useTimeout<string>(
        useCallback(
            (text: string): void => {
                if (text.length >= 3) {
                    startSuggestionsPromise([
                        axios
                            .get<{ suggestions: IValidateItem[] }>(
                                `https://api.getAddress.io/autocomplete/${text}?api-key=${apiKey}&all=true&fuzzy=false`,
                            )
                            .then((res) => {
                                setAllSuggestions(res.data.suggestions || []);
                                return res;
                            }),
                    ]);
                } else {
                    resetPromise();
                    setSelectedSuggestion(undefined);
                    setAllSuggestions([]);
                }
            },
            [setAllSuggestions, startSuggestionsPromise, apiKey, setSelectedSuggestion, resetPromise],
        ),
        DEBOUNCE_DELAY,
    );

    const handleSearch = useCallback(
        (text: string) => {
            start(text);
        },
        [start],
    );

    const options = useMemo(() => {
        const suggestions = allSuggestions.map<ISelectOption>((address): ISelectOption<IValidateItem> => {
            return {
                label: address.address,
                value: address.address,
                data: address,
            };
        });

        return suggestions;
    }, [allSuggestions]);

    const [showModal] = useModalEx<IInputLoqateValue | undefined>(
        (props, hideModal) => {
            const handleOkCLick = (e: { name: string; value: IInputLoqateValue }): void => {
                hideModal();
                onChange && onChange({ name: e.name, value: e.value });
            };
            return (
                <ManualAddressModal
                    name={name}
                    value={props || EMPTY_LOQATE_ADDRESS}
                    onBlur={onBlur}
                    onCloseClick={hideModal}
                    onOkClick={handleOkCLick}
                    onCancelClick={hideModal}
                />
            );
        },
        [name, onBlur, onChange],
    );

    const handleEnterAddressManuallyClick = useCallback(() => {
        showModal(value);
    }, [value, showModal]);

    const handleSelect = (e: string): void => {
        const address = allSuggestions.find((address) => {
            return address.address === e;
        });

        setSelectedAddress(address?.address);

        if (address) {
            axios.get<IGetItem>(`https://api.getAddress.io/get/${address?.id}?api-key=${apiKey}`).then((res) => {
                onChange &&
                    onChange({
                        name,
                        value: {
                            addressLine1: res.data.line_1,
                            addressLine2: res.data.line_2,
                            addressLine3: res.data.line_3,
                            city: res.data.town_or_city,
                            country: CONST.DEFAULT_COUNTRY_ABBR, // BE have validation max 2 sybmols (abbr) res.data.country,
                            label: address.address,
                            originalLabel: `${address.address}`,
                            postCode: res.data.postcode,
                        },
                    });
            });
        }
    };

    const handleDropDownVisibilityChange = useCallback((open: boolean) => {
        setIsOpen(open);
        if (!open) {
            return;
        }
    }, []);

    // block scrolling on mobile when dropdown is open contains options
    useEffect(() => {
        if (!isDownMD) {
            return;
        }

        // prevent body from scrolling when dropdown is open and contains options
        if (isOpen && options.length > 0) {
            document.body.style.height = '100%';
            document.body.style.overflowY = 'hidden';
        } else {
            // remove anti-scroll styles
            document.body.style.removeProperty('height');
            document.body.style.removeProperty('overflow-y');
        }
    }, [isOpen, options, isDownMD]);

    return (
        <Field
            css={styles.field}
            {...rest}
            className={className}
            name={`${name}`}
            errorMessage={errorMessage}
            helperText={<div>{helperText}</div>}
        >
            <Global styles={styles.dropDownBox} />

            <AutoComplete
                css={styles.addressInput}
                tw="w-full"
                options={options}
                onSearch={handleSearch}
                onSelect={handleSelect}
                value={selectedAddress ? selectedAddress : undefined}
                allowClear
                notFoundContent="Address not found"
                onDropdownVisibleChange={handleDropDownVisibilityChange}
                dropdownRender={(prev): JSX.Element => {
                    if (rest.readOnly) {
                        return <></>;
                    }
                    if (allSuggestions.length === 0) {
                        if (suggestionsSuccess || suggestionsError) {
                            return (
                                <div tw="relative px-0.5">
                                    <div css={styles.popup}>
                                        <div
                                            tw="flex text-black h-[34px] mb-2 cursor-pointer"
                                            onClick={handleEnterAddressManuallyClick}
                                        >
                                            <Icon tw="p-2" icon={PlusCircleOutlined} />
                                            <div tw="pt-1">Enter address manually</div>
                                        </div>
                                        <StatusTagV2 tw="w-full" name="noAddress" status="warning">
                                            We couldn't find your address, please enter it in manually.
                                        </StatusTagV2>
                                    </div>
                                </div>
                            );
                        } else {
                            return <></>;
                        }
                    }
                    return <div>{prev}</div>;
                }}
                clearIcon={
                    <div tw="mt-[-1px]" onClick={handleClearClick}>
                        <Icon tw="w-4 h-4 text-black" icon={CloseCircleOutlined} />
                    </div>
                }
            >
                <Input tw="w-full max-w-none" name="addressSearch" placeholder={placeholder} />
            </AutoComplete>
        </Field>
    );
};

const styles = {
    addressInput: css`
        .ant-select-item-option {
            font-weight: 300;
            padding: 8px 12px;
            &:not(:last-child) {
                border-bottom: 1px solid ${THEME.GRAY_300};
            }
        }
        && .ant-select-selector {
            height: 48px;
            border: 1px solid ${THEME.GRAY_300};
            border-radius: 4px;
            display: block;
            width: 100%;
            transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
            overflow: hidden;
            padding: 0 11px;
            :hover {
              border: 1px solid ${THEME.GRAY_300};
            }
        }
        && .ant-select-selection-search {
            display: flex;
            height: 100%;
        }
        && .ant-select-selection-search-input {
            font-weight: 300;
        }

        && .ant-select-item-option-selected {
            display: flex;
            justify-content: space-between;
            position: relative;
            background-color: ${THEME.WHITE};
            color: black;
        }

        && .ant-select-item-option-active {
            background-color: ${THEME.GRAY_200};
        }

        &.ant-select-focused {
            && .ant-select-selector {
                outline-color: transparent;
                outline-style: none;
            }
        }

        .ant-input-focused {
            box-shadow: none;
            border: 1px solid black;
        }

        .ant-select-selection-search-input {
            border: none;
            padding: 0;
            max-width: 100%;
            box-shadow: none;

            &:focused {
                border: none;
                outline: none;
            }
        }

        .ant-select-selection-placeholder {
            text-align: left;
            top: 2px;
            right: auto;
        }
    `,
    popup: css`
        width: 100%;
        background-color: white;
        position: relative;
        margin: 2px 1px 1px 0px;
        border-radius: 2px;
        padding: 8px;
        display: flex;
        flex-direction: column;
    `,
    dropDownBox: css`
        && .ant-select-item-option-selected {
            display: flex;
            justify-content: space-between;
            position: relative;
            background-color: ${THEME.WHITE} !important;
            color: black;
            font-weight: 400 !important;
        }
        && .ant-select-dropdown {
            border-radius: 4px;
            border: 1px solid ${THEME.GRAY_300};
            padding: 0;
            margin-top: 6px;
        }
        && .ant-select-dropdown-empty {
            border: none;
        }

        && .ant-select-item-option {
            font-weight: 300;
            padding: 8px 12px;
            &:not(:last-child) {
                border-bottom: 1px solid ${THEME.GRAY_300};
            }
        }
    `,
    field: css`
    && .field-footer {
      margin: 0px;
  }
    `
};
